import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Check, Trash2, ClipboardCheck, FileText, CalendarPlus } from "lucide-react";
import { api, shortDate, TASK_TYPES, TASK_TYPE_LABEL, WORK_STATUS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FormRenderer from "@/components/FormRenderer";
import { toast } from "sonner";

export default function Work() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [docTask, setDocTask] = useState(null);
  const [tab, setTab] = useState("all");

  const load = () => {
    const params = {};
    if (["cnat", "clinical_training_manager", "rcsm"].includes(user.role)) params.assignee_id = user.id;
    api.get("/tasks", { params }).then((r) => {
      // RCSM also sees what they allocated
      if (user.role === "rcsm") {
        api.get("/tasks", { params: { assigned_by_id: user.id } }).then((r2) => {
          const map = {}; [...r.data, ...r2.data].forEach((t) => (map[t.id] = t));
          setTasks(Object.values(map));
        });
      } else setTasks(r.data);
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = tab === "all" ? tasks : tasks.filter((t) => t.status === tab);
  const canAllocate = ["admin", "rcsm", "hr", "cnat", "clinical_training_manager"].includes(user.role);

  const setStatus = async (t, status) => { await api.put(`/tasks/${t.id}`, { status }); load(); };
  const del = async (id) => { await api.delete(`/tasks/${id}`); toast.success("Work removed"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Work Allocation</h1>
          <p className="mt-1 text-sm text-slate-500">Requested, due, scheduled and completed work.</p>
        </div>
        {canAllocate && (
          <Button data-testid="add-task-button" onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="mr-1.5 h-4 w-4" /> Allocate work
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1 w-fit">
        {["all", "requested", "due", "scheduled", "completed"].map((s) => (
          <button key={s} data-testid={`work-tab-${s}`} onClick={() => setTab(s)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize ${tab === s ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-50"}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filtered.length === 0 && <p className="text-slate-400">No work in this view.</p>}
        {filtered.map((t) => {
          const st = WORK_STATUS[t.status] || WORK_STATUS.due;
          const overdue = t.status !== "completed" && t.scheduled_date && new Date(t.scheduled_date) < new Date();
          return (
            <div key={t.id} data-testid="task-card" className={`rounded-xl border bg-white p-5 shadow-sm ${overdue ? "border-red-200" : "border-slate-200"}`}>
              <div className="flex items-start justify-between">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-50 text-orange-600"><ClipboardCheck className="h-4 w-4" /></div>
                <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${st.cls}`}>{overdue ? "Overdue" : st.label}</span>
              </div>
              <p className="mt-3 font-heading font-semibold text-slate-900">{t.title}</p>
              <button onClick={() => t.employee_id && navigate(`/people/${t.employee_id}`)} className="text-sm text-slate-500 hover:text-orange-600 hover:underline">{t.employee_name}</button>
              <div className="mt-2 space-y-1 text-xs text-slate-500">
                <p>Type: {TASK_TYPE_LABEL[t.task_type] || t.task_type}</p>
                {t.assignee_name && <p>Allocated to: {t.assignee_name}</p>}
                {t.reason && <p>Reason: {t.reason}</p>}
                <p>Scheduled: {t.scheduled_date ? shortDate(t.scheduled_date) : <span className="italic">awaiting date</span>}</p>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(t.can_edit ?? t.can_complete) && t.status !== "completed" && (
                  <Button data-testid="fill-document-button" size="sm" variant="outline" onClick={() => setDocTask(t)}>
                    <FileText className="mr-1 h-3.5 w-3.5" /> Fill & complete
                  </Button>
                )}
                {(t.can_edit ?? t.can_complete) && t.status === "completed" && (
                  <Button data-testid="update-document-button" size="sm" variant="outline" onClick={() => setDocTask(t)}>
                    <FileText className="mr-1 h-3.5 w-3.5" /> Update
                  </Button>
                )}
                {t.status === "completed" && <span className="text-xs text-emerald-600">✓ {shortDate(t.completed_date)}</span>}
                {["admin", "rcsm", "hr", "clinical_training_manager"].includes(user.role) && (
                  <button onClick={() => del(t.id)} className="ml-auto rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <TaskDialog open={open} onOpenChange={setOpen} onSaved={load} user={user} />
      <WorkDocDialog task={docTask} onClose={() => setDocTask(null)} onSaved={load} />
    </div>
  );
}

function WorkDocDialog({ task, onClose, onSaved }) {
  const [body, setBody] = useState("");
  const [answers, setAnswers] = useState({});
  const [scheduled, setScheduled] = useState("");
  useEffect(() => {
    if (task) { setBody(task.document_body || ""); setAnswers(task.document_answers || {}); setScheduled(task.scheduled_date || ""); }
  }, [task]);
  if (!task) return null;
  const hasFields = Array.isArray(task.document_fields) && task.document_fields.length > 0;

  const save = async (complete) => {
    if (complete && hasFields) {
      const missing = task.document_fields.filter((f) => f.required && (answers[f.id] == null || answers[f.id] === "" || (Array.isArray(answers[f.id]) && answers[f.id].length === 0)));
      if (missing.length) { toast.error(`Please answer: ${missing[0].label}`); return; }
    }
    const payload = hasFields ? { document_answers: answers } : { document_body: body };
    if (scheduled) payload.scheduled_date = scheduled;
    if (complete) payload.status = "completed";
    await api.put(`/tasks/${task.id}`, payload);
    toast.success(complete ? "Work completed" : "Saved");
    onClose();
    onSaved();
  };

  return (
    <Dialog open={!!task} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto esp-scroll" data-testid="work-doc-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{task.title}</DialogTitle>
          <DialogDescription>Fill in the form, add a date that fits your ROTA, then complete.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Date (fits your ROTA)</Label>
            <Input className="mt-1.5" type="date" data-testid="work-doc-date" value={scheduled} onChange={(e) => setScheduled(e.target.value)} />
          </div>
          {hasFields ? (
            <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-3" data-testid="work-doc-form">
              <FormRenderer fields={task.document_fields} answers={answers} onChange={setAnswers} />
            </div>
          ) : (
            <div>
              <Label className="text-xs uppercase text-slate-500">Document</Label>
              <Textarea className="mt-1.5 min-h-[220px] font-mono text-sm" data-testid="work-doc-body" value={body} onChange={(e) => setBody(e.target.value)} />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="work-doc-save" onClick={() => save(false)}>Save</Button>
            <Button data-testid="work-doc-complete" onClick={() => save(true)} className="bg-orange-500 hover:bg-orange-600">Mark completed</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TaskDialog({ open, onOpenChange, onSaved, user }) {
  const [employees, setEmployees] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [form, setForm] = useState({ title: "", employee_id: "", assignee_id: "", task_type: "supervision", reason: "" });

  useEffect(() => {
    if (!open) return;
    api.get("/employees").then((r) => setEmployees(r.data));
    Promise.all([
      api.get("/users", { params: { role: "cnat" } }),
      api.get("/users", { params: { role: "rcsm" } }),
    ]).then(([c, r]) => setAssignees([...c.data, ...r.data]));
    setForm({ title: "", employee_id: "", assignee_id: "", task_type: "supervision", reason: "" });
  }, [open]);

  const setType = (v) => setForm((f) => ({ ...f, task_type: v, title: TASK_TYPE_LABEL[v] || f.title }));

  const save = async () => {
    if (!form.title || !form.employee_id || !form.assignee_id) { toast.error("Title, employee and assignee are required"); return; }
    if (form.task_type === "supervision" && !form.reason) { toast.error("Please add a reason for the supervision"); return; }
    await api.post("/tasks", form); // no date at allocation — assignee adds it
    toast.success("Work allocated");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Allocate work</DialogTitle>
          <DialogDescription>The assignee will add a date that fits their ROTA.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Type</Label>
            <Select value={form.task_type} onValueChange={setType}>
              <SelectTrigger className="mt-1.5" data-testid="task-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TASK_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Title</Label>
            <Input className="mt-1.5" data-testid="task-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Employee</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="task-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Allocate to</Label>
            <Select value={form.assignee_id} onValueChange={(v) => setForm((f) => ({ ...f, assignee_id: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="task-assignee-select"><SelectValue placeholder="Select CNAT / RCSM" /></SelectTrigger>
              <SelectContent>{assignees.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.task_type === "supervision" && (
            <div>
              <Label className="text-xs uppercase text-slate-500">Reason for supervision</Label>
              <Textarea className="mt-1.5" data-testid="task-reason-input" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="task-save-button" onClick={save} className="bg-orange-500 hover:bg-orange-600">Allocate</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
